import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PriceInformation, SkuPriceValue } from 'microshop-api';
import services from 'services';
import { AppDispatch, RootState } from 'store';

export type PriceType = 'retail' | 'retailAndCustomer' | 'retailAsCustomerPrice' | 'customer' | 'none';

export type ThunkConf = {
    dispatch: AppDispatch;
    state: RootState;
};

const PRICE_CURRENCY_ACTION = 'price/currency';
export const customerPrice = createAsyncThunk<void, PriceInformation, ThunkConf>(
    'price/customerPrice',
    async (skuPrice, { getState, dispatch }) => {
        const customerPrices = getState().price.customerPrices;
        const price = skuPrice.sku && customerPrices[skuPrice.sku];
        if (!price) {
            dispatch(fetchCustomerPrice(skuPrice));
        }
    },
);

export const fetchCustomerPrice = createAsyncThunk<any[], PriceInformation, ThunkConf>(
    'price/fetchCustomerPrice',
    async (skuPrice) => {
        const response = await services.assortment.getPrice([{ sku: skuPrice.sku, inboxItems: skuPrice.inboxItems }]);
        return response;
    },
);

export type CustomerPrices = {
    [key: string]: SkuPriceValue | null;
};

export type PriceFormat = {
    prefix?: string;
    prefixBox?: string;
    prefixRetail?: string;
    prefixSales?: string;
};

interface PriceState {
    customerPrices: CustomerPrices;
    customerPricesPending: { [key: string]: boolean };
    currencies: string[];
    currency?: string;
    priceFormat?: PriceFormat;

    priceType: PriceType;
}
export const initialState: PriceState = {
    customerPrices: {},
    customerPricesPending: {},
    currencies: [],
    priceType: 'customer',
};

const priceSlice = createSlice({
    name: 'price',
    initialState,
    reducers: {
        currenciesSet(state, action: PayloadAction<string[]>) {
            state.currencies = action.payload;
        },
        priceFormatSet(state, action: PayloadAction<PriceFormat>) {
            state.priceFormat = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(fetchCustomerPrice.pending, (state, action) => {
            const sku = action.meta.arg.sku;

            if (sku) {
                state.customerPricesPending[sku] = true;
            }
        });
        builder.addCase(fetchCustomerPrice.fulfilled, (state, action) => {
            const sku = action.meta.arg.sku;

            if (sku) {
                const priceArray = action.payload;
                const price = priceArray.length ? priceArray[0] : null;

                state.customerPrices[sku] = price;
                state.customerPricesPending[sku] = false;
            }
        });
        builder.addCase(fetchCustomerPrice.rejected, (state, action) => {
            const sku = action.meta.arg.sku;

            if (sku) {
                state.customerPricesPending[sku] = false;
            }
        });
    },
});

// API
export { PRICE_CURRENCY_ACTION };
export const { currenciesSet, priceFormatSet } = priceSlice.actions;
export default priceSlice.reducer;

// * Price Selectors
const selectShoudLazyLoad = (state: RootState) => state.shop.shop?.isB2BShop;
const selectCustomerPrices = (state: RootState) => state.price.customerPrices;
const selectCustomerPricesPending = (state: RootState) => state.price.customerPricesPending;
const selectPriceInfo = (_: RootState, price: PriceInformation) => price;

export const selectCustomerPrice = createSelector(
    selectCustomerPrices,
    selectPriceInfo,
    selectShoudLazyLoad,
    (prices, priceInfo, shouldLazyLoad) => {
        if (!priceInfo) return null;

        const price = !shouldLazyLoad ? priceInfo.customer : priceInfo.sku ? prices[priceInfo.sku] : null;

        if (!price) return null;

        return price;
    },
);

export const selectCustomerPricePending = createSelector(
    selectCustomerPricesPending,
    selectPriceInfo,
    selectShoudLazyLoad,
    (pending, priceInfo, shouldLazyLoad) => {
        if (!priceInfo?.sku || !shouldLazyLoad) return false;
        const hasTried = pending.hasOwnProperty(priceInfo.sku);
        if (hasTried) return pending[priceInfo.sku];
        return true;
    },
);

export const selectPriceValue = createSelector(selectPriceInfo, (priceInfo) => {
    const isBox = !!(priceInfo.inboxItems && priceInfo.inboxItems > 1);
    return isBox && priceInfo.box ? priceInfo.box : priceInfo;
});
